import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ApiResponse, SuccessResponse } from 'further-types/api';
import { Api } from 'further-types/admin-user-organisation-membership';
import { api } from 'lib/httpClient';
import trpcApi from 'lib/trpcClient';
import { useNotification } from 'hooks/ui/useNotification';
import useApiExceptionHandler from 'hooks/ui/useApiExceptionHandler';
import { AdminUserRealm } from 'further-types/admin-user';
import useCurrentUser from 'hooks/data/currentUser/useCurrentUser';

const KEY = 'admin-user-organisation-membership';

export function useMutateAdminUserOrganisation(adminUserId?: string) {
  const queryClient = useQueryClient();
  const apiUtils = trpcApi.useUtils();
  const notification = useNotification();
  const { profile } = useCurrentUser();
  const handleApiException = useApiExceptionHandler();

  const create = useMutation({
    mutationFn: async (
      body: Api.CreateUserOrganisationMembershipRequestBody,
    ) => {
      const { data } = await api.post<ApiResponse<SuccessResponse>>(
        `admin-user-organisation-membership/${adminUserId}`,
        body,
      );
      return data.data;
    },
    onSuccess: async () => {
      notification.success('User added to organisation.');
      await queryClient.invalidateQueries({ queryKey: [KEY, adminUserId] });
      if (adminUserId === profile.data?._id) {
        // invalidate profile if self-editing
        await apiUtils.adminUser.profile.invalidate();
      }
    },
    onError: (error: Error) => handleApiException(error),
  });

  const remove = useMutation({
    mutationFn: async (
      body: Api.RemoveUserOrganisationMembershipRequestBody,
    ) => {
      await api.delete<ApiResponse<SuccessResponse>>(
        `admin-user-organisation-membership/${adminUserId}`,
        { data: body },
      );
    },
    onSuccess: async () => {
      notification.success('User removed from organisation.');
      await queryClient.invalidateQueries({ queryKey: [KEY, adminUserId] });
      if (adminUserId === profile.data?._id) {
        // invalidate if self-editing
        await apiUtils.adminUser.profile.invalidate();
      }
    },
    onError: (error: Error) => handleApiException(error),
  });

  return {
    create,
    remove,
  };
}

export function useOrganisationMemberships(adminUserId?: string) {
  return useQuery<Api.GetUserOrganisationMembershipsResponseBody>({
    queryKey: [KEY, adminUserId],
    queryFn: async () => {
      const { data } = await api.get<
        ApiResponse<Api.GetUserOrganisationMembershipsResponseBody>
      >(`admin-user-organisation-membership/${adminUserId}`);
      return data.data;
    },
    enabled: !!adminUserId,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 60,
  });
}

export function useAvailableOrganisations() {
  return useQuery<Api.GetAvailableOrganisationsResponseBody>({
    queryKey: ['admin-user-available-organisations'],
    queryFn: async () => {
      const { data } = await api.get<
        ApiResponse<Api.GetAvailableOrganisationsResponseBody>
      >(`admin-user-organisation-membership/available-organisations`);
      return data.data;
    },
  });
}

export function useRolesForOrganisation(
  realm: AdminUserRealm,
  firmId?: string,
) {
  return useQuery<Api.GetAvailableRolesForOrganisationResponseBody>({
    queryKey: ['admin-user-roles-for-organisation', realm, firmId],
    queryFn: async () => {
      const { data } = await api.post<
        ApiResponse<Api.GetAvailableRolesForOrganisationResponseBody>
      >(`admin-user-organisation-membership/available-roles-for-organisation`, {
        realm,
        firmId,
      });
      return data.data;
    },
    enabled: !!realm,
  });
}
